import { Container, Card, Table, Alert, Button, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatSampleDateTime } from '../utils/dateFnsLocale';
import { useRef, useEffect, type ReactNode } from 'react';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import { useAdminStorageViewAll } from '../hooks/useAdminStorageViewAll';
import { sampleService } from '../services/sample.service';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import SampleManagerNavigation from '../components/samples/SampleManagerNavigation';
import { SampleManagerPageHeading } from '../components/samples/SampleManagerPageHeading';
import { SamplePanelCardHeader } from '../components/samples/SamplePanelCardHeader';
import { SampleFactRow } from '../components/samples/SampleDetailFactsPanels';
import { SITE_NAME } from '../constants/siteSeo';
import { joinLabeledValue } from '../utils/joinLabeledValue';
import { formatUserName } from '../utils/name.util';
import {
  formatEntityDisplayName,
  softBreakIdentifier,
} from '../utils/softBreakIdentifier';
import { sortAndEnrichUsageRecords } from '../utils/sampleUsageRecords';
import {
  formatUsageRecordActionLabel,
  usageActionBadgeBg,
} from '../utils/sampleUsageActionDisplay';
import { formatSampleTypeDisplay } from '../utils/sampleTypeLabels';
import { formatSampleQuantityWithUnit } from '../utils/sampleUnitLabels';
import {
  readSampleRouterState,
  resolveUsageHistorySampleId,
  writeSampleUsageHistorySession,
  stripSampleRouterStateKeys,
  navigateToSampleDetail,
} from '../utils/samplePageNavigation';

type PersonLike = {
  firstName?: string;
  lastName?: string;
  username?: string;
} | null | undefined;

function displayPersonName(
  person: PersonLike,
  language: string,
  fallback?: string | null
): string {
  const formatted = person
    ? formatUserName(person.firstName, person.lastName, language)
    : '';
  return formatted || person?.username || fallback || '-';
}

export default function UsageHistory() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const sampleIdRef = useRef<number | null>(null);
  const routerStateStrippedRef = useRef(false);
  if (sampleIdRef.current === null) {
    sampleIdRef.current = resolveUsageHistorySampleId(location);
    if (sampleIdRef.current != null) {
      writeSampleUsageHistorySession(sampleIdRef.current);
    }
  }
  const sampleId = sampleIdRef.current;
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { adminViewAll } = useAdminStorageViewAll(isAdmin);

  useEffect(() => {
    if (routerStateStrippedRef.current) return;
    if (readSampleRouterState(location).usageHistorySampleId != null) {
      routerStateStrippedRef.current = true;
      stripSampleRouterStateKeys(navigate, location, ['usageHistorySampleId']);
    }
  }, [location, navigate]);

  const { data: sampleData, isLoading: isLoadingSample, isError: isErrorSample } = useQuery({
    queryKey: ['samples', 'detail', sampleId, adminViewAll],
    queryFn: () =>
      sampleService.getById(sampleId!, { adminViewAll: adminViewAll || undefined }),
    enabled: sampleId != null,
  });

  useEffect(() => {
    const name = sampleData?.data?.name;
    const id = sampleData?.data?.id;
    if (!name || id == null) return;
    const prev = document.title;
    const displayName = formatEntityDisplayName(name, t);
    document.title = `${displayName} · ${t('usage_history.nav_short', { defaultValue: '使用履歴' })} · ${SITE_NAME}`;
    return () => {
      document.title = prev;
    };
  }, [sampleData?.data?.name, sampleData?.data?.id, t]);

  const shell = (children: ReactNode) => (
    <Container fluid className="mt-2 px-3 px-md-4" id="sample-usage-history-page">
      <SampleManagerNavigation />
      {children}
    </Container>
  );

  if (sampleId == null) {
    const globalHistoryLabel = t('usage_history.open_global_history', {
      defaultValue: '全体の使用履歴へ',
    });
    const globalHistoryTitle = t('usage_history.open_global_history_title', {
      defaultValue: 'ナビ「履歴」と同じ、全サンプルの使用記録一覧を開く',
    });
    const pageTitle = t('usage_history.title');
    return shell(
      <>
        <SampleManagerPageHeading
          shortLabel={pageTitle}
          fullLabel={pageTitle}
          showFullOnMd
          iconClassName="fas fa-history"
        />
        <Alert
          variant="warning"
          className="mb-0 py-3 px-3 d-flex flex-column flex-sm-row align-items-sm-center gap-2 justify-content-between"
        >
          <span>{t('usage_history.no_selection')}</span>
          <div className="d-flex flex-wrap gap-2 flex-shrink-0 align-self-start align-self-sm-center">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => navigate('/samples/usage-records')}
              aria-label={globalHistoryLabel}
              title={globalHistoryTitle}
            >
              {globalHistoryLabel}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/samples')}
              aria-label={t('sample.back_to_list')}
              title={t('sample.back_to_list')}
            >
              {t('sample.back_to_list')}
            </Button>
          </div>
        </Alert>
      </>
    );
  }

  if (isLoadingSample) {
    return shell(
      <div className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </Spinner>
      </div>
    );
  }

  if (isErrorSample || !sampleData?.data) {
    return shell(
      <>
        <Alert variant="danger">{t('sample.load_failed')}</Alert>
        <Button variant="secondary" onClick={() => navigate('/samples')} className="mt-3">
          {t('sample.back_to_list')}
        </Button>
      </>
    );
  }

  const sample = sampleData.data;
  const recordsWithRemaining =
    sample.usageRecords && sample.usageRecords.length > 0
      ? sortAndEnrichUsageRecords(sample.usageRecords, sample.initialQuantity)
      : [];
  const storageRoom = sample.storageRoom as
    | { name?: string; location?: string | null; type?: string; temperature?: string }
    | null
    | undefined;
  const roomName = storageRoom?.name || '-';
  const roomLocation = storageRoom?.location || '-';

  return shell(
    <>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-start gap-2 mb-2">
        <SampleManagerPageHeading
          shortLabel={t('usage_history.title')}
          fullLabel={joinLabeledValue(
            t('usage_history.title'),
            softBreakIdentifier(sample.name, t)
          )}
          showFullOnMd
          iconClassName="fas fa-history"
          className="mb-0 min-w-0"
          trailing={
            <CollapsibleHelpSection
              compact
              titleKey="usage_history.how_to_use_title"
              descriptionKey="usage_history.how_to_use_desc"
              stepsKey={[
                'usage_history.how_to_use_step1',
                'usage_history.how_to_use_step2',
                'usage_history.how_to_use_step3',
              ]}
              storageKey="usage_history"
            />
          }
        />
        <Button
          variant="outline-secondary"
          size="sm"
          className="flex-shrink-0"
          onClick={() => navigateToSampleDetail(navigate, sampleId)}
        >
          <i className="fas fa-arrow-left me-1" aria-hidden="true" />
          {t('sample.back_to_detail')}
        </Button>
      </div>

      <Row className="mb-3 g-2">
        <Col md={6}>
          <Card className="h-100 border-0 shadow-sm sample-panel-card">
            <SamplePanelCardHeader icon="fa-flask" title={t('usage_history.sample_details')} />
            <Card.Body className="p-2 p-md-3">
              <Table bordered size="sm" className="mb-0 sample-detail-facts-table">
                <tbody>
                  <SampleFactRow label={t('common.id')}>{sample.id}</SampleFactRow>
                  <SampleFactRow label={t('sample.sample_name')}>
                    <span
                      className="text-break"
                      title={formatEntityDisplayName(sample.name, t)}
                    >
                      {softBreakIdentifier(sample.name, t)}
                    </span>
                  </SampleFactRow>
                  <SampleFactRow label={t('sample.sample_type')}>
                    {formatSampleTypeDisplay(sample.sampleType, t)}
                  </SampleFactRow>
                  <SampleFactRow
                    label={t('sample.bsl_level_short', { defaultValue: 'BSL' })}
                    labelTitle={t('sample.bsl_level')}
                  >
                    {sample.bslLevel || '-'}
                  </SampleFactRow>
                  <SampleFactRow label={t('sample.initial_quantity')}>
                    {formatSampleQuantityWithUnit(
                      sample.initialQuantity || 0,
                      sample.unit,
                      t
                    )}
                  </SampleFactRow>
                  <SampleFactRow
                    label={t('sample.creator', { defaultValue: '作製者' })}
                    labelTitle={t('sample.maker_name')}
                  >
                    {displayPersonName(
                      sample.user,
                      i18n.language,
                      sample.creatorDisplayName || t('common.system')
                    )}
                  </SampleFactRow>
                  <SampleFactRow label={t('common.created_at')}>
                    {formatSampleDateTime(sample.createdAt, i18n.language)}
                  </SampleFactRow>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100 border-0 shadow-sm sample-panel-card">
            <SamplePanelCardHeader icon="fa-warehouse" title={t('usage_history.storage_info')} />
            <Card.Body className="p-2 p-md-3">
              <Table bordered size="sm" className="mb-0 sample-detail-facts-table">
                <tbody>
                  <SampleFactRow label={t('storage_room.name')}>
                    <span
                      className="text-break"
                      title={formatEntityDisplayName(roomName, t)}
                    >
                      {softBreakIdentifier(roomName, t)}
                    </span>
                  </SampleFactRow>
                  <SampleFactRow label={t('storage_room.location')}>
                    <span
                      className="text-break"
                      title={formatEntityDisplayName(roomLocation, t)}
                    >
                      {softBreakIdentifier(roomLocation, t)}
                    </span>
                  </SampleFactRow>
                  <SampleFactRow label={t('storage_room.storage_type')}>
                    {storageRoom?.type || '-'}
                  </SampleFactRow>
                  <SampleFactRow label={t('storage_room.field_temperature_label')}>
                    {storageRoom?.temperature || '-'}
                  </SampleFactRow>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm sample-panel-card">
        <SamplePanelCardHeader
          icon="fa-list"
          title={t('usage_history.usage_history_with_creation')}
        />
        <Card.Body className="p-0">
          {recordsWithRemaining.length === 0 ? (
            <Alert variant="info" className="m-3 mb-3" role="status">
              {t('usage_history.no_history')}
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th className="text-nowrap">{t('usage_record.table_header_usage_date')}</th>
                    <th className="text-nowrap">{t('usage_record.table_header_action')}</th>
                    <th>{t('usage_record.table_header_detail')}</th>
                    <th className="text-nowrap">{t('usage_record.table_header_quantity')}</th>
                    <th className="text-nowrap">{t('usage_record.table_header_remaining')}</th>
                    <th className="text-nowrap">{t('usage_record.table_header_recorded_by')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recordsWithRemaining.map((record) => {
                    const actionText = formatUsageRecordActionLabel(record, t);
                    const detailRaw =
                      ((record.purpose as string) || (record.notes as string) || '-').trim() ||
                      '-';
                    const qty =
                      record.isCreationRecord ||
                      record.quantity === null ||
                      record.quantity === undefined
                        ? '-'
                        : formatSampleQuantityWithUnit(record.quantity, sample.unit, t);
                    const remaining =
                      record.calculatedRemaining != null
                        ? formatSampleQuantityWithUnit(
                            record.calculatedRemaining,
                            sample.unit,
                            t
                          )
                        : '-';
                    return (
                      <tr
                        key={record.id as number}
                        className={record.isCreationRecord ? 'table-success' : undefined}
                      >
                        <td className="text-nowrap">
                          {record.usageDate
                            ? formatSampleDateTime(record.usageDate as string, i18n.language)
                            : '-'}
                        </td>
                        <td>
                          <Badge
                            bg={
                              record.isCreationRecord
                                ? 'success'
                                : usageActionBadgeBg(record.action)
                            }
                          >
                            {actionText}
                          </Badge>
                        </td>
                        <td className="text-break" title={detailRaw}>
                          {softBreakIdentifier(detailRaw)}
                        </td>
                        <td className="text-end text-nowrap">{qty}</td>
                        <td className="text-end fw-bold text-primary text-nowrap">{remaining}</td>
                        <td className="text-nowrap">
                          {displayPersonName(
                            record.user as PersonLike,
                            i18n.language,
                            record.recordedBy as string | undefined
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
}
